import React, { useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Send, CheckSquare, Mail, Eye, RefreshCw, Users, UserPlus, Clock, CalendarCheck, RotateCcw } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

export default function SchedulingInvitations() {
  const [stagedFamilies, setStagedFamilies] = useState([]);
  const [mainFamilies, setMainFamilies] = useState([]);
  const [selectedFamilies, setSelectedFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('welcome');
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('needs_welcome');

  useEffect(() => {
    fetchAllFamilies();
    fetchTemplates();
  }, []);

  useEffect(() => {
    // Clear selection and set appropriate template when switching tabs
    setSelectedFamilies([]);
    if (activeTab === 'needs_welcome') {
      setSelectedTemplate('welcome');
    } else if (activeTab === 'needs_schedule_invite') {
      setSelectedTemplate('schedule_invitation');
    } else if (activeTab === 'resend') {
      setSelectedTemplate('welcome'); // Default to welcome for resend
    }
  }, [activeTab]);

  const fetchAllFamilies = async () => {
    setLoading(true);
    try {
      // Fetch staged families (New Families)
      const stagedResponse = await api.get('/families/staged');
      setStagedFamilies(stagedResponse.data);
      
      // Fetch main list families
      const mainResponse = await api.get('/families');
      setMainFamilies(mainResponse.data);
    } catch (error) {
      toast.error('Failed to fetch families');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/announcements/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  // Get families based on active tab
  const getCurrentFamilies = () => {
    const allFamilies = [...stagedFamilies, ...mainFamilies];
    
    if (activeTab === 'needs_welcome') {
      // Families who haven't received welcome email yet
      return allFamilies.filter(f => !f.welcome_email_sent);
    } else if (activeTab === 'needs_schedule_invite') {
      // Families who received welcome email, clicked "Ready to Schedule", but haven't received schedule invite
      return allFamilies.filter(f => 
        f.welcome_email_sent && 
        f.ready_to_schedule && 
        !f.scheduling_invited
      );
    } else if (activeTab === 'resend') {
      // All families who have received at least one email (welcome or schedule invite)
      return allFamilies.filter(f => f.welcome_email_sent || f.scheduling_invited);
    }
    return [];
  };

  // Count helpers
  const getNeedsWelcomeCount = () => {
    const allFamilies = [...stagedFamilies, ...mainFamilies];
    return allFamilies.filter(f => !f.welcome_email_sent).length;
  };

  const getNeedsScheduleInviteCount = () => {
    const allFamilies = [...stagedFamilies, ...mainFamilies];
    return allFamilies.filter(f => 
      f.welcome_email_sent && 
      f.ready_to_schedule && 
      !f.scheduling_invited
    ).length;
  };

  const getResendCount = () => {
    const allFamilies = [...stagedFamilies, ...mainFamilies];
    return allFamilies.filter(f => f.welcome_email_sent || f.scheduling_invited).length;
  };

  const handleSelectAll = (checked) => {
    const currentFamilies = getCurrentFamilies();
    if (checked) {
      setSelectedFamilies(currentFamilies.map(f => f.id));
    } else {
      setSelectedFamilies([]);
    }
  };

  const handleSelectFamily = (familyId, checked) => {
    if (checked) {
      setSelectedFamilies([...selectedFamilies, familyId]);
    } else {
      setSelectedFamilies(selectedFamilies.filter(id => id !== familyId));
    }
  };

  const handlePreviewTemplate = async () => {
    setLoadingPreview(true);
    try {
      const response = await api.get(`/email-templates/${selectedTemplate}/preview`);
      setPreviewHtml(response.data.html);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSendInvitations = async () => {
    if (selectedFamilies.length === 0) {
      toast.error('Please select at least one family');
      return;
    }

    setSending(true);
    try {
      if (selectedTemplate === 'welcome') {
        // Use the send-welcome-emails endpoint for welcome emails
        const response = await api.post('/send-welcome-emails', {
          family_ids: selectedFamilies
        });
        toast.success(`Welcome emails sent to ${response.data.sent_count} families!`);
        if (response.data.failed_count > 0) {
          toast.error(`Failed to send to ${response.data.failed_count} families`);
        }
      } else {
        // Use the generic send with template endpoint for schedule invitations
        const response = await api.post('/families/send-email-invitation', {
          family_ids: selectedFamilies,
          template_id: selectedTemplate
        });
        toast.success(response.data.message || `Emails sent to ${selectedFamilies.length} families!`);
      }
      setSelectedFamilies([]);
      fetchAllFamilies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  // Get template options based on active tab
  const getTemplateOptions = () => {
    if (activeTab === 'needs_welcome') {
      return [
        { id: 'welcome', name: 'Welcome Email', description: 'Welcome with login credentials and instructions' },
      ];
    } else if (activeTab === 'needs_schedule_invite') {
      return [
        { id: 'schedule_invitation', name: 'Schedule Invitation', description: 'Invite to schedule delivery' },
      ];
    } else if (activeTab === 'resend') {
      return [
        { id: 'welcome', name: 'Welcome Email', description: 'Resend welcome with login credentials' },
        { id: 'schedule_invitation', name: 'Schedule Invitation', description: 'Resend scheduling invitation' },
      ];
    }
    return [];
  };

  // Get badge for a family
  const getFamilyBadge = (family) => {
    if (!family.welcome_email_sent) {
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-300">
          <Mail className="w-3 h-3 mr-1" />
          Needs Welcome Email
        </Badge>
      );
    }
    if (family.welcome_email_sent && !family.ready_to_schedule) {
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300">
          <CheckSquare className="w-3 h-3 mr-1" />
          Welcome Sent - Awaiting Response
        </Badge>
      );
    }
    if (family.ready_to_schedule && !family.scheduling_invited) {
      return (
        <Badge variant="outline" className="text-purple-600 border-purple-300">
          <Clock className="w-3 h-3 mr-1" />
          Ready to Schedule - Needs Invite
        </Badge>
      );
    }
    if (family.scheduling_invited && !family.has_scheduled_delivery) {
      return (
        <Badge variant="outline" className="text-indigo-600 border-indigo-300">
          <Send className="w-3 h-3 mr-1" />
          Schedule Invite Sent
        </Badge>
      );
    }
    if (family.has_scheduled_delivery) {
      return (
        <Badge variant="outline" className="text-green-600 border-green-300">
          <CalendarCheck className="w-3 h-3 mr-1" />
          Delivery Scheduled
        </Badge>
      );
    }
    return null;
  };

  // Get emails sent badges for resend tab
  const getEmailsSentBadges = (family) => {
    const badges = [];
    if (family.welcome_email_sent) {
      badges.push(
        <Badge key="welcome" variant="outline" className="text-green-600 border-green-300 text-xs">
          ✓ Welcome Sent
        </Badge>
      );
    }
    if (family.scheduling_invited) {
      badges.push(
        <Badge key="schedule" variant="outline" className="text-green-600 border-green-300 text-xs">
          ✓ Schedule Invite Sent
        </Badge>
      );
    }
    return badges;
  };

  const currentFamilies = getCurrentFamilies();
  const templateOptions = getTemplateOptions();

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="scheduling-invitations-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">Send Invitations</h1>
              <p className="text-stone-600">Send welcome emails and scheduling invitations to families</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={fetchAllFamilies}
                disabled={loading}
                className="rounded-full"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={handleSendInvitations}
                disabled={selectedFamilies.length === 0 || sending}
                className="rounded-full"
                data-testid="send-invitations-button"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : `Send to ${selectedFamilies.length} Families`}
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className={`border-amber-200 cursor-pointer transition-all ${activeTab === 'needs_welcome' ? 'bg-amber-100 ring-2 ring-amber-400' : 'bg-amber-50 hover:bg-amber-100'}`}
              onClick={() => setActiveTab('needs_welcome')}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-700">{getNeedsWelcomeCount()}</p>
                <p className="text-sm text-amber-600">Needs Welcome Email</p>
              </CardContent>
            </Card>
            <Card className={`border-purple-200 cursor-pointer transition-all ${activeTab === 'needs_schedule_invite' ? 'bg-purple-100 ring-2 ring-purple-400' : 'bg-purple-50 hover:bg-purple-100'}`}
              onClick={() => setActiveTab('needs_schedule_invite')}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-purple-700">{getNeedsScheduleInviteCount()}</p>
                <p className="text-sm text-purple-600">Ready for Schedule Invite</p>
              </CardContent>
            </Card>
            <Card className={`border-blue-200 cursor-pointer transition-all ${activeTab === 'resend' ? 'bg-blue-100 ring-2 ring-blue-400' : 'bg-blue-50 hover:bg-blue-100'}`}
              onClick={() => setActiveTab('resend')}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">{getResendCount()}</p>
                <p className="text-sm text-blue-600">Resend Emails</p>
              </CardContent>
            </Card>
          </div>

          {/* Email Template Selection */}
          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Template
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label htmlFor="template-select" className="mb-2 block">Selected Template</Label>
                  <select
                    id="template-select"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    data-testid="template-select"
                  >
                    {templateOptions.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} - {template.description}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  variant="outline"
                  onClick={handlePreviewTemplate}
                  disabled={loadingPreview}
                  data-testid="preview-template-button"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {loadingPreview ? 'Loading...' : 'Preview'}
                </Button>
              </div>
              <div className="mt-3 text-sm text-stone-500">
                {activeTab === 'needs_welcome' && (
                  <span className="text-blue-600">ℹ️ Welcome emails include login credentials and instructions for the family to click "I'm Ready to Schedule"</span>
                )}
                {activeTab === 'needs_schedule_invite' && (
                  <span className="text-purple-600">ℹ️ These families have indicated they're ready - send them the scheduling invitation so they can pick a delivery time</span>
                )}
                {activeTab === 'resend' && (
                  <span className="text-blue-600">ℹ️ Resend emails to families who have already received them. Select the email type above.</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tabs for different invitation types */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="needs_welcome" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Needs Welcome ({getNeedsWelcomeCount()})
              </TabsTrigger>
              <TabsTrigger value="needs_schedule_invite" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Ready for Invite ({getNeedsScheduleInviteCount()})
              </TabsTrigger>
              <TabsTrigger value="resend" className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4" />
                Resend ({getResendCount()})
              </TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-4 text-stone-600">Loading families...</p>
              </div>
            ) : currentFamilies.length === 0 ? (
              <Card className="border-stone-200 shadow-card">
                <CardContent className="py-12 text-center">
                  <CheckSquare className="w-16 h-16 text-green-300 mx-auto mb-4" />
                  <p className="text-stone-600 mb-2">
                    {activeTab === 'resend' ? 'No families to resend to yet' : 'All caught up!'}
                  </p>
                  <p className="text-sm text-stone-500">
                    {activeTab === 'needs_welcome' 
                      ? 'All families have received their welcome emails.'
                      : activeTab === 'needs_schedule_invite'
                      ? 'No families are currently waiting for a scheduling invitation.'
                      : 'No families have received emails yet.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-stone-200 shadow-card">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>
                      {activeTab === 'needs_welcome' 
                        ? `Families Needing Welcome Email (${currentFamilies.length})`
                        : activeTab === 'needs_schedule_invite'
                        ? `Families Ready for Schedule Invitation (${currentFamilies.length})`
                        : `Resend Emails to Families (${currentFamilies.length})`
                      }
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="select-all"
                        checked={selectedFamilies.length === currentFamilies.length && currentFamilies.length > 0}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                      <label htmlFor="select-all" className="text-sm cursor-pointer">
                        Select All
                      </label>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="families-list">
                    {currentFamilies.map((family) => (
                      <div
                        key={family.id}
                        className={`p-4 border rounded-lg flex items-center gap-4 hover:bg-stone-50 transition-colors ${
                          selectedFamilies.includes(family.id) ? 'border-primary ring-2 ring-primary/20' : 'border-stone-200'
                        }`}
                        data-testid={`family-${family.id}`}
                      >
                        <Checkbox
                          id={`family-${family.id}`}
                          checked={selectedFamilies.includes(family.id)}
                          onCheckedChange={(checked) => handleSelectFamily(family.id, checked)}
                          data-testid={`checkbox-${family.id}`}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-lg">
                            {family.parent_first_name} {family.parent_last_name}
                          </p>
                          <div className="flex gap-3 text-sm text-stone-600 mt-1 flex-wrap">
                            <span>{family.email}</span>
                            <span>•</span>
                            <span>{family.location_type}: {family.location_detail}</span>
                            <span>•</span>
                            <span>{family.children?.length || 0} child{(family.children?.length || 0) !== 1 ? 'ren' : ''}</span>
                            <span>•</span>
                            <span className="text-stone-400">Joined: {formatDate(family.created_at)}</span>
                          </div>
                          {family.ready_to_schedule_at && activeTab !== 'resend' && (
                            <p className="text-xs text-purple-600 mt-1">
                              Ready to schedule since: {formatDate(family.ready_to_schedule_at)}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 items-end">
                          {activeTab === 'resend' ? (
                            getEmailsSentBadges(family)
                          ) : (
                            <>
                              {getFamilyBadge(family)}
                              {!family.in_main_list && (
                                <Badge variant="outline" className="text-stone-500 border-stone-300">
                                  <UserPlus className="w-3 h-3 mr-1" />
                                  New Family
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </Tabs>
        </div>
      </div>

      {/* Template Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle>Email Template Preview</DialogTitle>
            <DialogDescription>
              Preview of "{templates[selectedTemplate]?.name || selectedTemplate}" template
            </DialogDescription>
          </DialogHeader>
          <div 
            className="border rounded-lg p-4 bg-white"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
